import { create } from "zustand";
import { agents, company } from "./mockData";
import {
  loadOnboarding,
  saveOnboarding,
  clearOnboardingStorage,
  deriveProfile,
} from "./onboarding";
import api from "./api";

const initialAgentsEnabled = Object.fromEntries(agents.map((a) => [a.id, true]));

// Persist Settings choices that affect debate behaviour across reloads.
const AGENTS_ENABLED_KEY = "ideaai_agents_enabled_v1";
const MODE_KEY = "ideaai_mode_v1";

const safeRead = (key) => {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const safeWrite = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* swallow */
  }
};

const hydratedAgentsEnabled = (() => {
  const raw = safeRead(AGENTS_ENABLED_KEY);
  if (!raw || typeof raw !== "object") return initialAgentsEnabled;
  const merged = { ...initialAgentsEnabled };
  for (const a of agents) {
    if (a.id in raw) merged[a.id] = !!raw[a.id];
  }
  return merged;
})();

const hydratedMode = (() => {
  const raw = safeRead(MODE_KEY);
  return raw === "bootstrap" || raw === "funding" ? raw : null;
})();

// Neutral placeholder used BEFORE server onboarding hydration completes and
// after `clearOnboarding()`. Must NOT leak landing-page mock names (e.g.
// "Lumen AI") into the signed-in shell — see Phase G regression.
const defaultProfile = {
  name: "Untitled Co.",
  tagline: "",
  stage: "PRE-SEED",
  country: "US",
  incorporationDate: "2026-03-01",
  burn: 48000,
  mrr: 12000,
  teamSize: 1,
};

const hydratedOnboarding = loadOnboarding();
const initialMode = hydratedMode ?? company.mode;
const initialProfile = hydratedOnboarding
  ? deriveProfile(hydratedOnboarding)
  : defaultProfile;

export const useStore = create((set) => ({
  // Operating mode (Bootstrap / Funding) — persisted to localStorage.
  mode: initialMode,
  setMode: (mode) => {
    safeWrite(MODE_KEY, mode);
    set({ mode });
  },

  // Agent seats — persisted to localStorage.
  agentsEnabled: hydratedAgentsEnabled,
  toggleAgent: (id) =>
    set((s) => {
      const next = { ...s.agentsEnabled, [id]: !s.agentsEnabled[id] };
      safeWrite(AGENTS_ENABLED_KEY, next);
      return { agentsEnabled: next };
    }),

  // Company profile (derived from onboarding).
  profile: initialProfile,
  setProfileField: (key, value) =>
    set((s) => ({ profile: { ...s.profile, [key]: value } })),

  // Global drawers
  hitlOpen: false,
  openHitl: () => set({ hitlOpen: true }),
  closeHitl: () => set({ hitlOpen: false }),

  // Command palette
  cmdkOpen: false,
  openCmdk: () => set({ cmdkOpen: true }),
  closeCmdk: () => set({ cmdkOpen: false }),
  setCmdkOpen: (v) => set({ cmdkOpen: !!v }),

  // Tasks (live-mirrored from /api/tasks for HitlChip + HitlDrawer).
  tasks: [],
  setTasksList: (list) => set({ tasks: Array.isArray(list) ? list : [] }),
  updateTask: (id, patch) =>
    set((s) => ({
      tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })),

  // Debates (Phase B) — single live stream + currently-streaming agent id
  currentDebateSessionId: null,
  setCurrentDebateSessionId: (id) => set({ currentDebateSessionId: id }),
  currentlyStreamingAgentId: null,
  setCurrentlyStreamingAgentId: (id) => set({ currentlyStreamingAgentId: id }),
  isStreamingDebate: false,
  setIsStreamingDebate: (v) => set({ isStreamingDebate: !!v }),

  // Onboarding
  onboarding: hydratedOnboarding,
  onboardingHydrated: false,
  setOnboardingHydrated: (v) => set({ onboardingHydrated: !!v }),

  setOnboarding: (data) => {
    saveOnboarding(data);
    set({
      onboarding: data,
      profile: deriveProfile(data),
    });
  },

  // Hydrate the store from a server-side onboarding profile (no localStorage write).
  hydrateOnboardingFromServer: (data) => {
    if (!data) return;
    set({
      onboarding: data,
      profile: deriveProfile(data),
    });
  },

  // Persist the current local-only onboarding to the server (post-login migration).
  migrateOnboardingToServer: async () => {
    const { onboarding } = useStore.getState();
    if (!onboarding) return null;
    const payload = {
      user_name: onboarding.name || onboarding.user_name || "",
      user_email: onboarding.email || onboarding.user_email || null,
      company_name: onboarding.company || onboarding.company_name || "",
      company_tagline: onboarding.industry || onboarding.company_tagline || "",
      industry: onboarding.industry || "SaaS",
      stage: onboarding.stage || "preseed",
      goal: onboarding.goal || "",
      blockers: onboarding.blockers || [],
    };
    try {
      const { data } = await api.put("/api/onboarding", payload);
      clearOnboardingStorage();
      set({
        onboarding: data,
        profile: deriveProfile(data),
      });
      return data;
    } catch {
      return null;
    }
  },

  clearOnboarding: () => {
    clearOnboardingStorage();
    set({
      onboarding: null,
      profile: defaultProfile,
      mode: company.mode,
      tasks: [],
    });
  },

  onboardingOpen: false,
  onboardingThen: null,
  openOnboarding: (opts = {}) =>
    set({
      onboardingOpen: true,
      onboardingThen: typeof opts.then === "function" ? opts.then : null,
    }),
  closeOnboarding: () =>
    set({ onboardingOpen: false, onboardingThen: null }),
}));
